import { useCallback, useEffect, useState } from "react";

import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { FlagFailure } from "../constants/flag-failures";

/**
 * The read behind every flag surface: the library page, a client's chart, and the client list's
 * flag column.
 *
 * All three read one scope at a time, have to tell a first load apart from a refresh of the same
 * scope, and turn a failure into a message their own map words. What differs between them is the
 * query, the shape of the data, and the wording — so those are what a caller passes in.
 *
 * The flag-specific part is the failure contract: `read` answers with a `FlagFailure`, and a read
 * that throws is worded as `FlagFailure.LookupFailed`. Everything else about it is a scoped async
 * read, which is why the query arrives as a parameter.
 */

/**
 * What the last read produced, and the scope it produced it *for*.
 *
 * `scope` is `undefined` until there is a result, which `isFirstLoad` reads as "no answer for the
 * current scope yet".
 */
interface LastReadResult<TData, TFailure extends FlagFailure> {
  scope?: string;
  data?: TData;
  errorMessage: string | null;
  failureReason: TFailure | FlagFailure.LookupFailed | null;
}

export interface FlagReadResult<TData, TFailure extends FlagFailure = FlagFailure> {
  /** `undefined` until the current scope has an answer, and after a failed read. */
  data?: TData;
  /** There is nothing to show yet: the first read for this scope is still to come, or in flight. */
  isLoading: boolean;
  /** Any read is in flight. Drives a refresh control, which keeps the current rows on screen. */
  isRefreshing: boolean;
  /** The message to show in place of the data, or `null` when there is nothing to say. */
  errorMessage: string | null;
  /**
   * Why the last read of this scope failed, or `null` when it did not. Lets a caller act on the
   * reason itself, for example hiding a surface on a refused read.
   */
  failureReason: TFailure | FlagFailure.LookupFailed | null;
  /** Re-reads the current scope. */
  refresh: () => void;
}

interface FlagReadProps<TData, TFailure extends FlagFailure> {
  /**
   * One key for every value the query is made of, so a change to any of them reads as a new scope.
   */
  scope: string;
  /**
   * Runs the query. Memoize it on the same values `scope` is built from, so it stays in step with
   * the scope its answer will be filed under.
   */
  read: () => Promise<{ ok: true; data: TData } | { ok: false; reason: TFailure }>;
  /**
   * The caller's wording for each reason it can receive. `null` is a reason with nothing to say —
   * a refused read, where the surface renders nothing rather than reporting a fault.
   *
   * `LookupFailed` is required on top of the caller's own reasons: a `read` that throws rather than
   * answering is the same broken lookup, and it is worded the same way.
   *
   * `NoInfer` keeps the reasons coming from `read`. Without it a map that happens to cover more
   * than `read` can return would widen `TFailure`, and one covering exactly `read`'s reasons would
   * narrow it by whatever `LookupFailed` already accounts for.
   */
  failureMessages: Record<NoInfer<TFailure> | FlagFailure.LookupFailed, string | null>;
  /**
   * Whether the surface is showing. A surface that is gated off, or a drawer that is still closed,
   * passes `false` and the query is never made — flags are an accessory everywhere they appear, so
   * a hidden one costs nothing. Defaults to `true`.
   */
  enabled?: boolean;
  /**
   * Goes up when the data behind this scope changed somewhere else in the app. A new value re-reads
   * the scope the way `refresh` does, keeping the current rows on screen.
   */
  revision?: number;
}

const NO_RESULT: LastReadResult<never, never> = { errorMessage: null, failureReason: null };

export default function useFlagRead<TData, TFailure extends FlagFailure>({
  scope,
  read,
  failureMessages,
  enabled = true,
  revision = 0,
}: FlagReadProps<TData, TFailure>): FlagReadResult<TData, TFailure> {
  const [result, setResult] = useState<LastReadResult<TData, TFailure>>(NO_RESULT);
  const [isFetching, setIsFetching] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    // A surface that hides forgets its answer, so opening it again shows the spinner and reads
    // afresh rather than the flags it held the last time it was open.
    if (!enabled) {
      setResult(NO_RESULT);
      return;
    }

    let isStale = false;

    setIsFetching(true);

    const load = async () => {
      try {
        const response = await read();

        if (isStale) return;

        setResult({
          scope,
          data: response.ok ? response.data : undefined,
          errorMessage: response.ok ? null : failureMessages[response.reason],
          failureReason: response.ok ? null : response.reason,
        });
      } catch (error) {
        captureExceptionWithoutPhi("A flag read threw instead of returning a result", error, {
          scope,
        });

        if (isStale) return;

        setResult({
          scope,
          errorMessage: failureMessages[FlagFailure.LookupFailed],
          failureReason: FlagFailure.LookupFailed,
        });
      } finally {
        if (!isStale) setIsFetching(false);
      }
    };

    load();

    return () => {
      isStale = true;
    };
  }, [enabled, scope, read, refreshKey, revision, failureMessages]);

  /**
   * A result for another scope is not this scope's answer, so a scope change reads as a first
   * load rather than a refresh — the alternative is briefly showing one client's flags on another's
   * profile, or one practice's library under another's name. Changing the client or the practice
   * re-renders the hook without remounting it, so nothing else would clear them.
   */
  const isFirstLoad = result.scope !== scope;

  // A hidden surface has nothing to show, which is what its consumer renders the spinner for: the
  // one being opened reads as loading from its first render, before its read has even started.
  if (!enabled) {
    return {
      isLoading: true,
      isRefreshing: false,
      errorMessage: null,
      failureReason: null,
      refresh,
    };
  }

  return {
    data: isFirstLoad ? undefined : result.data,
    isLoading: isFirstLoad,
    isRefreshing: isFetching,
    errorMessage: isFirstLoad ? null : result.errorMessage,
    failureReason: isFirstLoad ? null : result.failureReason,
    refresh,
  };
}
